import React, { useState, useEffect, useCallback, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { callApi } from "../api/api";
import { useAccess } from "../context/AccessContext";
import { Calendar, ArrowLeft, PlusCircle, Trash, Trash2, RefreshCw, Clock, CheckCircle2, AlertCircle, XCircle, User, MessageSquare, Check, X, ShieldCheck, UserCheck } from "lucide-react";

const THEME = "rgb(37, 99, 235)";
const THEME_LIGHT = "rgba(37, 99, 235, 0.08)";

/* ─────────────────────────────────────────────────────────────
   CUSTOM DATE PICKER COMPONENT (100% Identical to Holidays.js)
   ───────────────────────────────────────────────────────────── */
function CustomDatePicker({
  value, // "YYYY-MM-DD"
  onChange, // function(val)
  placeholder = "Select date…",
  disabled = false,
  holidaysList = [], // Array of holiday date strings "YYYY-MM-DD" or holiday objects
  ptoList = [], // Array of existing user PTO / absent records
  id,
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  // 6 months max limit into the future
  const max6MonthsDate = new Date(today.getFullYear(), today.getMonth() + 6, today.getDate());

  const minYear = today.getFullYear();
  const minMonth = today.getMonth();

  const maxYear = max6MonthsDate.getFullYear();
  const maxMonth = max6MonthsDate.getMonth();

  const initialDate = value ? new Date(value) : today;
  const [navDate, setNavDate] = useState(initialDate);

  const holidaysSet = React.useMemo(() => {
    const set = new Set();
    if (Array.isArray(holidaysList)) {
      holidaysList.forEach((h) => {
        if (typeof h === "string") {
          set.add(h.split("T")[0]);
        } else if (h?.holidayDate) {
          set.add(String(h.holidayDate).split("T")[0]);
        }
      });
    }
    return set;
  }, [holidaysList]);

  const ptoSet = React.useMemo(() => {
    const set = new Set();
    if (Array.isArray(ptoList)) {
      ptoList.forEach((item) => {
        if (typeof item === "string") {
          set.add(item.split("T")[0]);
        } else if (item?.absentDate) {
          set.add(String(item.absentDate).split("T")[0]);
        }
      });
    }
    return set;
  }, [ptoList]);

  useEffect(() => {
    if (value) {
      setNavDate(new Date(value));
    }
  }, [value]);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleToggle = () => {
    if (disabled) return;
    setOpen((prev) => !prev);
  };

  const selectDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    onChange(`${y}-${m}-${d}`);
    setOpen(false);
  };

  const handleClear = () => {
    onChange("");
    setOpen(false);
  };

  const handleToday = () => {
    const t = new Date();
    selectDate(t);
  };

  const year = navDate.getFullYear();
  const month = navDate.getMonth();

  const canGoPrev = year > minYear || (year === minYear && month > minMonth);
  const canGoNext = year < maxYear || (year === maxYear && month < maxMonth);

  const prevMonth = () => {
    if (!canGoPrev) return;
    setNavDate(new Date(year, month - 1, 1));
  };

  const nextMonth = () => {
    if (!canGoNext) return;
    setNavDate(new Date(year, month + 1, 1));
  };

  const firstDayIndex = new Date(year, month, 1).getDay();
  const totalDays = new Date(year, month + 1, 0).getDate();
  const prevTotalDays = new Date(year, month, 0).getDate();

  const daysGrid = [];

  for (let i = firstDayIndex - 1; i >= 0; i--) {
    daysGrid.push({
      date: new Date(year, month - 1, prevTotalDays - i),
      isCurrentMonth: false,
    });
  }

  for (let i = 1; i <= totalDays; i++) {
    daysGrid.push({
      date: new Date(year, month, i),
      isCurrentMonth: true,
    });
  }

  const remaining = 42 - daysGrid.length;
  for (let i = 1; i <= remaining; i++) {
    daysGrid.push({
      date: new Date(year, month + 1, i),
      isCurrentMonth: false,
    });
  }

  const formatDateDisplay = (val) => {
    if (!val) return "";
    const [y, m, d] = val.split("-");
    const dateObj = new Date(Number(y), Number(m) - 1, Number(d));
    if (isNaN(dateObj.getTime())) return val;
    return dateObj.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const selectedStr = value ? value : "";
  const displayVal = formatDateDisplay(selectedStr);

  return (
    <div
      ref={wrapRef}
      onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); }}
      style={{ position: "relative", width: "100%", zIndex: open ? 1000 : 1 }}
      id={id}
    >
      <button
        type="button"
        className="ado-focusable"
        onClick={handleToggle}
        disabled={disabled}
        style={{
          ...dp.trigger,
          opacity: disabled ? 0.55 : 1,
          cursor: disabled ? "not-allowed" : "pointer",
          borderColor: open ? THEME : "#cbd5e1",
          boxShadow: open ? `0 0 0 3px ${THEME_LIGHT}` : "none",
        }}
      >
        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {displayVal ? (
            <span>{displayVal}</span>
          ) : (
            <span style={{ color: "#94a3b8" }}>{placeholder}</span>
          )}
        </span>
        <svg
          style={{ width: 18, height: 18, flexShrink: 0, color: "#64748b" }}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      </button>

      {open && (
        <div style={{
          ...s.panel,
          padding: "10px",
          width: "250px",
          boxSizing: "border-box",
          top: "calc(100% + 4px)"
        }}>
          <div style={dp.header}>
            <button
              type="button"
              onClick={prevMonth}
              disabled={!canGoPrev}
              style={{
                ...dp.navBtn,
                opacity: canGoPrev ? 1 : 0.3,
                cursor: canGoPrev ? "pointer" : "not-allowed"
              }}
              title={canGoPrev ? "Previous Month" : "Min month limit"}
            >
              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 16, height: 16 }}>
                <polyline points="12 5 7 10 12 15" />
              </svg>
            </button>
            <span style={dp.navTitle}>
              {monthNames[month]} {year}
            </span>
            <button
              type="button"
              onClick={nextMonth}
              disabled={!canGoNext}
              style={{
                ...dp.navBtn,
                opacity: canGoNext ? 1 : 0.3,
                cursor: canGoNext ? "pointer" : "not-allowed"
              }}
              title={canGoNext ? "Next Month" : "6 months limit reached"}
            >
              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 16, height: 16 }}>
                <polyline points="8 5 13 10 8 15" />
              </svg>
            </button>
          </div>

          <div style={dp.weekdaysGrid}>
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
              <span key={day} style={dp.weekday}>
                {day}
              </span>
            ))}
          </div>

          <div style={dp.daysGrid}>
            {daysGrid.map(({ date, isCurrentMonth }, idx) => {
              const dateY = date.getFullYear();
              const dateM = String(date.getMonth() + 1).padStart(2, "0");
              const dateD = String(date.getDate()).padStart(2, "0");
              const dateStr = `${dateY}-${dateM}-${dateD}`;

              const dayOfWeek = date.getDay(); // 0 = Sun, 6 = Sat
              const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
              const isHoliday = holidaysSet.has(dateStr);
              const isAlreadyAbsent = ptoSet.has(dateStr);

              const cellDateObj = new Date(dateY, date.getMonth(), date.getDate());
              const isBeforeToday = cellDateObj < startOfToday;
              const isAfter6Months = cellDateObj > max6MonthsDate;
              const isOutOfRange = isBeforeToday || isAfter6Months;

              const isDisabledCell = isWeekend || isHoliday || isAlreadyAbsent || isOutOfRange;

              let disabledReason = "";
              if (isAlreadyAbsent) disabledReason = "Already Marked Absent (Disabled)";
              else if (isWeekend) disabledReason = "Weekend (Disabled)";
              else if (isHoliday) disabledReason = "Holiday (Disabled)";
              else if (isBeforeToday) disabledReason = "Past Date";
              else if (isAfter6Months) disabledReason = "Beyond 6-Month Limit";

              const isSelected = dateStr === selectedStr;
              const isToday =
                date.getDate() === today.getDate() &&
                date.getMonth() === today.getMonth() &&
                date.getFullYear() === today.getFullYear();

              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => {
                    if (isDisabledCell) return;
                    selectDate(date);
                  }}
                  disabled={isDisabledCell}
                  title={disabledReason || dateStr}
                  style={{
                    ...dp.dayCell,
                    color: isSelected
                      ? "#ffffff"
                      : isAlreadyAbsent
                        ? "#dc2626"
                        : isDisabledCell
                          ? "#94a3b8"
                          : isCurrentMonth
                            ? "#0f172a"
                            : "#cbd5e1",
                    background: isSelected
                      ? THEME
                      : isAlreadyAbsent
                        ? "#fee2e2"
                        : isDisabledCell
                          ? "#f8fafc"
                          : isToday
                            ? THEME_LIGHT
                            : "transparent",
                    fontWeight: isSelected || isToday || isAlreadyAbsent ? "600" : "400",
                    border: isToday && !isSelected ? `1px solid ${THEME}` : "none",
                    cursor: isDisabledCell ? "not-allowed" : "pointer",
                    opacity: isDisabledCell ? (isAlreadyAbsent ? 0.75 : 0.45) : 1,
                    textDecoration: isHoliday || isWeekend || isAlreadyAbsent ? "line-through" : "none",
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected && !isDisabledCell) {
                      e.currentTarget.style.background = "#f1f5f9";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected && !isDisabledCell) {
                      e.currentTarget.style.background = isToday ? THEME_LIGHT : "transparent";
                    }
                  }}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>

          <div style={dp.footer}>
            <button type="button" onClick={handleClear} style={dp.footerBtn}>
              Clear
            </button>
            <button type="button" onClick={handleToday} style={{ ...dp.footerBtn, color: THEME, fontWeight: "600" }}>
              Today
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   MAIN PTO COMPONENT
   ───────────────────────────────────────────────────────────── */
export default function PTO({ onBack }) {
  const { instance, accounts } = useMsal();
  const currentUserEmail = accounts[0]?.username || accounts[0]?.name || "User";

  const access = useAccess();
  const isTeamMember = access?.isTeamMember ?? false;
  const isAdmin = access?.isAdmin ?? false;
  const canReviewLeaves = isAdmin || !isTeamMember;

  const [activeTab, setActiveTab] = useState("apply"); // "apply" | "review"
  const [ptoList, setPtoList] = useState([]);
  const [allAbsentDates, setAllAbsentDates] = useState([]);
  const [holidaysList, setHolidaysList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // Leave Review Queue State (for upper hierarchy managers)
  const [pendingReviews, setPendingReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [reviewingId, setReviewingId] = useState(null);
  const [rejectModal, setRejectModal] = useState({ open: false, request: null, comment: "" });
  const [approveModal, setApproveModal] = useState({ open: false, request: null });
  const [cancelModal, setCancelModal] = useState({ open: false, request: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, item: null });

  // My Leave Requests State (Section 3: Approval Status & History)
  const [myRequests, setMyRequests] = useState([]);
  const [loadingMyRequests, setLoadingMyRequests] = useState(false);
  const [myCurrentPage, setMyCurrentPage] = useState(1);
  const [myRequestsPagination, setMyRequestsPagination] = useState({
    totalCount: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
  });
  const [cancellingRequestId, setCancellingRequestId] = useState(null);

  // A request can be cancelled if:
  // 1) Status is PENDING
  // 2) Status is APPROVED and starting date (fromDate) is strictly in the future (> today)
  const isRequestCancellable = useCallback((item) => {
    if (!item) return false;
    const statusUpper = (item.status || "").toUpperCase();
    if (statusUpper === "PENDING") return true;
    if (statusUpper === "APPROVED") {
      if (!item.fromDate) return false;
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
      const startStr = String(item.fromDate).split("T")[0];
      return startStr > todayStr;
    }
    return false;
  }, []);

  const hasCancellableRequests = myRequests.some(isRequestCancellable);

  // Pagination State for active absent records
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(5);
  const [gotoPageInput, setGotoPageInput] = useState("");
  const [paginationInfo, setPaginationInfo] = useState({
    totalCount: 0,
    page: 1,
    limit: 5,
    totalPages: 1,
  });

  // Form State
  const [selectionMode, setSelectionMode] = useState("today"); // "today" | "single" | "range"
  const [singleDate, setSingleDate] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [reason, setReason] = useState("");
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");

  const loadAllAbsentDates = useCallback(async () => {
    try {
      const emailQuery = currentUserEmail ? `&userEmail=${encodeURIComponent(currentUserEmail)}` : "";
      const res = await callApi(
        instance,
        accounts,
        "GET",
        `/api/assignments/pto?fromToday=true&page=1&limit=1000${emailQuery}`
      );
      const records = res?.data?.data || res?.data || [];
      const list = Array.isArray(records) ? records : [];
      setAllAbsentDates(list.map((r) => r.absentDate));
    } catch (err) {
      console.error("Failed to load active future absent dates for calendar:", err);
    }
  }, [instance, accounts, currentUserEmail]);

  const loadPtoRecords = useCallback(async (pageArg = 1) => {
    const pageToLoad = typeof pageArg === "number" ? pageArg : 1;
    setLoading(true);
    try {
      const emailQuery = currentUserEmail ? `&userEmail=${encodeURIComponent(currentUserEmail)}` : "";
      const res = await callApi(
        instance,
        accounts,
        "GET",
        `/api/assignments/pto?page=${pageToLoad}&limit=${pageSize}${emailQuery}`
      );

      const records = res?.data?.data || res?.data || [];
      const pagination = res?.data?.pagination || {
        totalCount: Array.isArray(records) ? records.length : 0,
        page: pageToLoad,
        limit: pageSize,
        totalPages: Math.ceil((Array.isArray(records) ? records.length : 0) / pageSize) || 1,
      };

      setPtoList(Array.isArray(records) ? records : []);
      setPaginationInfo(pagination);
      setCurrentPage(pagination.page || pageToLoad);
    } catch (err) {
      console.error("Failed to load PTO records:", err);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, currentUserEmail, pageSize]);

  const loadPendingReviews = useCallback(async () => {
    if (!currentUserEmail || !canReviewLeaves) return;
    setLoadingReviews(true);
    try {
      const res = await callApi(
        instance,
        accounts,
        "GET",
        `/api/assignments/pto/requests/review?approverEmail=${encodeURIComponent(currentUserEmail)}`
      );
      const list = res?.data?.data?.requests || res?.data?.requests || [];
      setPendingReviews(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Failed to load pending reviews for upper hierarchy:", err);
    } finally {
      setLoadingReviews(false);
    }
  }, [instance, accounts, currentUserEmail, canReviewLeaves]);

  const loadMyRequests = useCallback(async (pageArg = 1) => {
    if (!currentUserEmail) return;
    const pageToLoad = typeof pageArg === "number" ? pageArg : 1;
    setLoadingMyRequests(true);
    try {
      const res = await callApi(
        instance,
        accounts,
        "GET",
        `/api/assignments/pto/requests/my?userEmail=${encodeURIComponent(currentUserEmail)}&page=${pageToLoad}&limit=10`
      );
      const records = res?.data?.data || [];
      const pagination = res?.data?.pagination || {
        totalCount: records.length,
        page: pageToLoad,
        limit: 10,
        totalPages: 1,
      };
      setMyRequests(Array.isArray(records) ? records : []);
      setMyRequestsPagination(pagination);
      setMyCurrentPage(pagination.page || pageToLoad);
    } catch (err) {
      console.error("Failed to load my leave requests:", err);
    } finally {
      setLoadingMyRequests(false);
    }
  }, [instance, accounts, currentUserEmail]);

  const loadHolidays = useCallback(async () => {
    try {
      const year = new Date().getFullYear();
      const res = await callApi(instance, accounts, "GET", `/api/assignments/holidays?year=${year}`);
      const rawData = res?.data?.data || res?.data || [];
      const list = Array.isArray(rawData) ? rawData : [];
      setHolidaysList(list);
    } catch (err) {
      console.error("Failed to load holidays for PTO picker:", err);
    }
  }, [instance, accounts]);

  useEffect(() => {
    loadPtoRecords(1);
    loadHolidays();
    loadAllAbsentDates();
    loadPendingReviews();
    loadMyRequests(1);
  }, [loadPtoRecords, loadHolidays, loadAllAbsentDates, loadPendingReviews, loadMyRequests]);

  // Auto-dismiss alert notifications
  useEffect(() => {
    if (!formSuccess) return;
    const timer = setTimeout(() => {
      setFormSuccess("");
    }, 5000);
    return () => clearTimeout(timer);
  }, [formSuccess]);

  useEffect(() => {
    if (!formError) return;
    const timer = setTimeout(() => {
      setFormError("");
    }, 7000);
    return () => clearTimeout(timer);
  }, [formError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    setFormSuccess("");

    if (selectionMode === "single" && !singleDate) {
      setFormError("Please select an absent date.");
      return;
    }
    if (selectionMode === "range" && (!fromDate || !toDate)) {
      setFormError("Please select both From and To dates.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        selectionMode,
        absentDate: selectionMode === "single" ? singleDate : undefined,
        fromDate: selectionMode === "range" ? fromDate : undefined,
        toDate: selectionMode === "range" ? toDate : undefined,
        reason: reason.trim() || "Paid Time Off",
        userEmail: currentUserEmail,
        userName: accounts[0]?.name || null,
      };

      const res = await callApi(instance, accounts, "POST", "/api/assignments/pto/request", payload);
      const successMsg =
        (res?.data?.message && res.data.message !== "Success" ? res.data.message : null) ||
        res?.data?.data?.message ||
        "Leave request submitted for approval! Your reporting manager will review it.";
      setFormSuccess(successMsg);

      setSingleDate("");
      setFromDate("");
      setToDate("");
      setReason("");
      loadMyRequests(1);
      loadPendingReviews();
    } catch (err) {
      setFormError(err.message || "Failed to submit leave request.");
    } finally {
      setSaving(false);
    }
  };

  const openApproveModal = (reqItem) => {
    setApproveModal({ open: true, request: reqItem });
  };

  const handleConfirmApprove = async () => {
    if (!approveModal.request) return;
    const reqItem = approveModal.request;
    setReviewingId(reqItem.requestId);
    try {
      await callApi(instance, accounts, "POST", `/api/assignments/pto/requests/${reqItem.requestId}/review`, {
        decision: "APPROVED",
        approverEmail: currentUserEmail,
        approverName: accounts[0]?.name || currentUserEmail,
      });
      setFormSuccess(`Leave request for ${reqItem.userName || reqItem.userEmail} has been approved.`);
      setApproveModal({ open: false, request: null });
      loadPendingReviews();
      loadMyRequests(myCurrentPage);
      loadAllAbsentDates();
      loadPtoRecords(1);
    } catch (err) {
      setFormError("Failed to approve request: " + (err.message || err));
    } finally {
      setReviewingId(null);
    }
  };

  const openRejectModal = (reqItem) => {
    setRejectModal({ open: true, request: reqItem, comment: "" });
  };

  const handleConfirmReject = async () => {
    if (!rejectModal.request) return;
    setReviewingId(rejectModal.request.requestId);
    try {
      await callApi(instance, accounts, "POST", `/api/assignments/pto/requests/${rejectModal.request.requestId}/review`, {
        decision: "REJECTED",
        rejectionReason: rejectModal.comment.trim() || "Request not approved by manager",
        approverEmail: currentUserEmail,
        approverName: accounts[0]?.name || currentUserEmail,
      });
      setFormSuccess(`Leave request for ${rejectModal.request.userName || rejectModal.request.userEmail} has been rejected.`);
      setRejectModal({ open: false, request: null, comment: "" });
      loadPendingReviews();
      loadMyRequests(myCurrentPage);
    } catch (err) {
      setFormError("Failed to reject request: " + (err.message || err));
    } finally {
      setReviewingId(null);
    }
  };

  const openCancelModal = (item) => {
    setCancelModal({ open: true, request: item });
  };

  const handleConfirmCancel = async () => {
    if (!cancelModal.request) return;
    const requestId = cancelModal.request.requestId;
    setCancellingRequestId(requestId);
    try {
      await callApi(instance, accounts, "DELETE", `/api/assignments/pto/requests/${requestId}?userEmail=${encodeURIComponent(currentUserEmail)}`);
      setFormSuccess("Leave request cancelled successfully.");
      setCancelModal({ open: false, request: null });
      loadMyRequests(myCurrentPage);
      loadPendingReviews();
      loadAllAbsentDates();
      loadPtoRecords(currentPage);
    } catch (err) {
      const errMsg = err?.response?.data?.message || err?.message || err;
      setFormError("Failed to cancel request: " + errMsg);
      setCancelModal({ open: false, request: null });
    } finally {
      setCancellingRequestId(null);
    }
  };

  const openDeleteModal = (item) => {
    setDeleteModal({ open: true, item });
  };

  const handleConfirmDelete = async () => {
    if (!deleteModal.item) return;
    const absentId = deleteModal.item.absentId;
    setDeletingId(absentId);
    try {
      await callApi(instance, accounts, "DELETE", `/api/assignments/pto/${absentId}`);
      setFormSuccess("Absent record cancelled successfully.");
      setDeleteModal({ open: false, item: null });
      loadPtoRecords(currentPage);
      loadAllAbsentDates();
    } catch (err) {
      setFormError("Failed to delete PTO record: " + (err.message || err));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div style={s.page}>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        .ado-refresh-btn:hover {
          background: #f8fafc !important;
          border-color: #94a3b8 !important;
          color: #0f172a !important;
        }
        .pto-rejected-badge {
          position: relative;
          cursor: pointer;
        }
        .pto-rejected-badge .pto-rejection-tooltip {
          visibility: hidden;
          opacity: 0;
          position: absolute;
          bottom: calc(100% + 7px);
          left: 50%;
          transform: translateX(-50%) translateY(3px);
          background: #0f172a;
          color: #ffffff;
          padding: 5px 10px;
          border-radius: 6px;
          font-size: 11px;
          font-weight: 500;
          width: max-content;
          max-width: 220px;
          text-align: center;
          word-break: break-word;
          box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
          pointer-events: none;
          transition: opacity 0.15s ease, transform 0.15s ease, visibility 0.15s;
          z-index: 999;
          border: 1px solid rgba(255, 255, 255, 0.12);
        }
        .pto-rejected-badge .pto-rejection-tooltip::after {
          content: '';
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          border-width: 6px;
          border-style: solid;
          border-color: #0f172a transparent transparent transparent;
        }
        .pto-rejected-badge:hover .pto-rejection-tooltip {
          visibility: visible;
          opacity: 1;
          transform: translateX(-50%) translateY(0);
        }
      `}</style>
      {/* HEADER */}
      <div style={s.header}>
        <div style={s.breadcrumb}>
          <span>Document Index Hub</span>
          {" "}
          &gt;{" "}
          <span style={{ color: "#0f172a", fontWeight: "600" }}>Availability</span>
        </div>
        <h1 style={s.title}>Availability &amp; Leave</h1>
        <p style={s.sub}>{canReviewLeaves ? "Submit leave requests, process manager approvals, and view live status." : "Submit leave requests and view approval status."}</p>
      </div>

      {/* ALERTS */}
      {formError && (
        <div style={{ ...s.errorBox, display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <AlertCircle size={18} style={{ flexShrink: 0, color: "#dc2626" }} />
            <span>{formError}</span>
          </div>
          <button
            type="button"
            onClick={() => setFormError("")}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#991b1b",
              padding: "2px",
              display: "inline-flex",
              alignItems: "center",
              borderRadius: "4px",
              opacity: 0.8,
            }}
            title="Dismiss message"
          >
            <X size={16} />
          </button>
        </div>
      )}
      {formSuccess && (
        <div style={{ ...s.successBox, display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <CheckCircle2 size={18} style={{ flexShrink: 0, color: "#16a34a" }} />
            <span>{formSuccess}</span>
          </div>
          <button
            type="button"
            onClick={() => setFormSuccess("")}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#166534",
              padding: "2px",
              display: "inline-flex",
              alignItems: "center",
              borderRadius: "4px",
              opacity: 0.8,
            }}
            title="Dismiss message"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* TABS (Only shown for managers/admins who review requests) */}
      {canReviewLeaves && (
        <div style={{ display: "inline-flex", marginBottom: "24px", alignItems: "stretch", boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)" }}>
          <button
            type="button"
            onClick={() => setActiveTab("apply")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "9px 18px",
              borderTopLeftRadius: "8px",
              borderBottomLeftRadius: "8px",
              borderTopRightRadius: "0",
              borderBottomRightRadius: "0",
              border: activeTab === "apply" ? `1px solid ${THEME}` : "1px solid #cbd5e1",
              background: activeTab === "apply" ? THEME : "#ffffff",
              color: activeTab === "apply" ? "#ffffff" : "#334155",
              fontWeight: "600",
              fontSize: "13px",
              cursor: "pointer",
              transition: "all 0.15s ease",
              position: "relative",
              zIndex: activeTab === "apply" ? 2 : 1,
            }}
          >
            <Calendar size={15} color={activeTab === "apply" ? "#ffffff" : "#64748b"} />
            <span>Submit Request</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab("review")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "9px 18px",
              borderTopLeftRadius: "0",
              borderBottomLeftRadius: "0",
              borderTopRightRadius: "8px",
              borderBottomRightRadius: "8px",
              border: activeTab === "review" ? `1px solid ${THEME}` : "1px solid #cbd5e1",
              marginLeft: "-1px",
              background: activeTab === "review" ? THEME : "#ffffff",
              color: activeTab === "review" ? "#ffffff" : "#334155",
              fontWeight: "600",
              fontSize: "13px",
              cursor: "pointer",
              transition: "all 0.15s ease",
              position: "relative",
              zIndex: activeTab === "review" ? 2 : 1,
            }}
          >
            <ShieldCheck size={15} color={activeTab === "review" ? "#ffffff" : "#64748b"} />
            <span>Pending Reviews</span>
            <span
              style={{
                fontSize: "11px",
                fontWeight: "700",
                padding: "1px 7px",
                borderRadius: "10px",
                background: activeTab === "review" ? "rgba(255,255,255,0.25)" : (pendingReviews.length > 0 ? "#fef3c7" : "#f1f5f9"),
                color: activeTab === "review" ? "#ffffff" : (pendingReviews.length > 0 ? "#b45309" : "#64748b"),
              }}
            >
              {pendingReviews.length}
            </span>
          </button>
        </div>
      )}

      {/* SECTION 1: REQUEST LEAVE / MARK ABSENT FORM */}
      {activeTab === "apply" && (
        <div style={s.gridContainer}>
          {/* LEFT COLUMN: Add Form */}
          <div style={s.formColumn}>
            <div style={s.card}>
              <h3 style={s.cardTitle}>Mark Absent Date</h3>
              <form onSubmit={handleSubmit} style={s.form}>
                {/* Option Pills */}
                <div style={s.field}>
                  <label style={s.label}>Select Option</label>
                  <div style={{ display: "flex", width: "100%", alignItems: "stretch", boxShadow: "0 1px 2px rgba(0, 0, 0, 0.04)" }}>
                    <button
                      type="button"
                      onClick={() => setSelectionMode("today")}
                      style={{
                        flex: 1,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "0 8px",
                        height: "36px",
                        whiteSpace: "nowrap",
                        borderTopLeftRadius: "6px",
                        borderBottomLeftRadius: "6px",
                        borderTopRightRadius: "0",
                        borderBottomRightRadius: "0",
                        border: selectionMode === "today" ? `1px solid ${THEME}` : "1px solid #cbd5e1",
                        background: selectionMode === "today" ? THEME : "#ffffff",
                        color: selectionMode === "today" ? "#ffffff" : "#334155",
                        fontWeight: "600",
                        fontSize: "12.5px",
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                        position: "relative",
                        zIndex: selectionMode === "today" ? 2 : 1,
                      }}
                    >
                      Today
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectionMode("single")}
                      style={{
                        flex: 1,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "0 8px",
                        height: "36px",
                        whiteSpace: "nowrap",
                        borderRadius: "0",
                        border: selectionMode === "single" ? `1px solid ${THEME}` : "1px solid #cbd5e1",
                        marginLeft: "-1px",
                        background: selectionMode === "single" ? THEME : "#ffffff",
                        color: selectionMode === "single" ? "#ffffff" : "#334155",
                        fontWeight: "600",
                        fontSize: "12.5px",
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                        position: "relative",
                        zIndex: selectionMode === "single" ? 2 : 1,
                      }}
                    >
                      Single Date
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectionMode("range")}
                      style={{
                        flex: 1,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "0 8px",
                        height: "36px",
                        whiteSpace: "nowrap",
                        borderTopLeftRadius: "0",
                        borderBottomLeftRadius: "0",
                        borderTopRightRadius: "6px",
                        borderBottomRightRadius: "6px",
                        border: selectionMode === "range" ? `1px solid ${THEME}` : "1px solid #cbd5e1",
                        marginLeft: "-1px",
                        background: selectionMode === "range" ? THEME : "#ffffff",
                        color: selectionMode === "range" ? "#ffffff" : "#334155",
                        fontWeight: "600",
                        fontSize: "12.5px",
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                        position: "relative",
                        zIndex: selectionMode === "range" ? 2 : 1,
                      }}
                    >
                      Date Range
                    </button>
                  </div>
                </div>

                {/* Mode Specific Inputs */}
                {selectionMode === "today" && (
                  <div style={s.infoBox}>
                    <Clock size={16} color={THEME} />
                    <span>Marking absent for <strong>Today ({new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })})</strong></span>
                  </div>
                )}

                {selectionMode === "single" && (
                  <div style={s.field}>
                    <label style={s.label}>Absent Date</label>
                    <CustomDatePicker
                      value={singleDate}
                      onChange={setSingleDate}
                      placeholder="Select absent date…"
                      holidaysList={holidaysList}
                      ptoList={allAbsentDates}
                    />
                  </div>
                )}

                {selectionMode === "range" && (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                    <div style={s.field}>
                      <label style={s.label}>From Date</label>
                      <CustomDatePicker
                        value={fromDate}
                        onChange={setFromDate}
                        placeholder="Start date…"
                        holidaysList={holidaysList}
                        ptoList={allAbsentDates}
                      />
                    </div>
                    <div style={s.field}>
                      <label style={s.label}>To Date</label>
                      <CustomDatePicker
                        value={toDate}
                        onChange={setToDate}
                        placeholder="End date…"
                        holidaysList={holidaysList}
                        ptoList={allAbsentDates}
                      />
                    </div>
                  </div>
                )}

                {/* Reason Input */}
                <div style={s.field}>
                  <label style={s.label}>Reason / Comment (Optional)</label>
                  <input
                    type="text"
                    placeholder="e.g. Personal Leave, Vacation…"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    style={s.input}
                  />
                </div>

                <button
                  type="submit"
                  disabled={saving}
                  style={{
                    ...s.submitBtn,
                    opacity: saving ? 0.7 : 1,
                    cursor: saving ? "not-allowed" : "pointer"
                  }}
                >
                  <PlusCircle size={15} style={{ marginRight: 6 }} />
                  {saving ? "Submitting Request…" : "Submit Leave Request for Approval"}
                </button>
              </form>
            </div>
          </div>

          {/* RIGHT COLUMN: Leave Approval Status & History */}
          <div style={s.tableColumn}>
            <div style={s.card}>
              <div style={s.tableHeaderRow}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Calendar size={18} color={THEME} />
                  <h3 style={{ ...s.cardTitle, margin: 0 }}>
                    Leave Approval Status &amp; History ({myRequestsPagination.totalRecords || myRequests.length})
                  </h3>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <button
                    className="ado-refresh-btn"
                    style={s.refreshBtn}
                    onClick={() => loadMyRequests(myCurrentPage)}
                    disabled={loadingMyRequests}
                    title="Refresh Leave Approval Status & History"
                  >
                    <RefreshCw size={15} style={loadingMyRequests ? { animation: "spin 0.8s linear infinite" } : {}} />
                  </button>
                </div>
              </div>

              {myRequests.length === 0 && loadingMyRequests ? (
                <div style={s.tableContainer}>
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th style={s.th}>DATES</th>
                        <th style={s.th}>REASON</th>
                        <th style={{ ...s.th, textAlign: "center" }}>STATUS</th>
                        {hasCancellableRequests && <th style={s.thActions}>ACTIONS</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {Array.from({ length: 5 }).map((_, i) => (
                        <tr key={`skeleton-${i}`} style={s.tr}>
                          <td style={s.td}><div style={s.skeletonBar} /></td>
                          <td style={s.td}><div style={{ ...s.skeletonBar, width: "70%" }} /></td>
                          <td style={{ ...s.td, textAlign: "center" }}><div style={{ ...s.skeletonBar, width: "60px", margin: "0 auto" }} /></td>
                          {hasCancellableRequests && <td style={s.tdActions}><div style={{ ...s.skeletonBar, width: "40px", margin: "0 auto" }} /></td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : myRequests.length === 0 && !loadingMyRequests ? (
                <div style={s.emptyBox}>No leave requests submitted yet.</div>
              ) : (
                <div style={{ ...s.tableContainer, position: "relative" }}>
                  {loadingMyRequests && (
                    <div style={s.overlayLoader}>
                      <RefreshCw size={20} color={THEME} style={{ animation: "spin 0.8s linear infinite" }} />
                      <span>Loading requests…</span>
                    </div>
                  )}
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th style={s.th}>DATES</th>
                        <th style={s.th}>REASON</th>
                        <th style={{ ...s.th, textAlign: "center" }}>STATUS</th>
                        {hasCancellableRequests && <th style={s.thActions}>ACTIONS</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {myRequests.map((item) => {
                        const isCancelling = cancellingRequestId === item.requestId;
                        const statusUpper = (item.status || "PENDING").toUpperCase();

                        return (
                          <tr key={item.requestId} style={s.tr}>
                            <td style={s.tdBold}>
                              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                <Calendar size={14} color="#64748b" />
                                <span>
                                  {item.fromDate}
                                  {item.fromDate !== item.toDate && ` → ${item.toDate}`}
                                </span>
                              </div>
                              <div style={{ marginTop: "2px" }}>
                                <span style={{ fontSize: "11px", fontWeight: "600", padding: "1px 6px", borderRadius: "4px", background: "#f1f5f9", color: "#475569" }}>
                                  {item.daysCount} {item.daysCount === 1 ? "day" : "days"}
                                </span>
                              </div>
                            </td>
                            <td style={s.td}>
                              <span title={item.reason || "Paid Time Off"}>
                                {item.reason || "Paid Time Off"}
                              </span>
                            </td>
                            <td style={{ ...s.td, textAlign: "center" }}>
                              {statusUpper === "APPROVED" && (
                                <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", fontSize: "11px", fontWeight: "700", padding: "3px 9px", borderRadius: "12px", background: "#dcfce7", color: "#15803d" }}>
                                  <Check size={12} strokeWidth={3} />
                                  APPROVED
                                </span>
                              )}
                              {statusUpper === "REJECTED" && (
                                <span
                                  className="pto-rejected-badge"
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    fontSize: "11px",
                                    fontWeight: "700",
                                    padding: "3px 9px",
                                    borderRadius: "12px",
                                    background: "#fee2e2",
                                    color: "#b91c1c",
                                    cursor: item.rejectionReason ? "help" : "default",
                                  }}
                                  title={item.rejectionReason || "REJECTED"}
                                >
                                  <X size={12} strokeWidth={3} />
                                  REJECTED
                                  {item.rejectionReason && (
                                    <span className="pto-rejection-tooltip">
                                      {item.rejectionReason}
                                    </span>
                                  )}
                                </span>
                              )}
                              {statusUpper === "PENDING" && (
                                <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", fontSize: "11px", fontWeight: "700", padding: "3px 9px", borderRadius: "12px", background: "#fef3c7", color: "#b45309" }}>
                                  <Clock size={12} strokeWidth={3} />
                                  PENDING
                                </span>
                              )}
                            </td>
                            {hasCancellableRequests && (
                              <td style={s.tdActions}>
                                {isRequestCancellable(item) ? (
                                  <button
                                    type="button"
                                    onClick={() => openCancelModal(item)}
                                    disabled={isCancelling}
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "4px",
                                      padding: "4px 9px",
                                      borderRadius: "6px",
                                      border: "1px solid #fecaca",
                                      background: "#fef2f2",
                                      color: "#dc2626",
                                      fontSize: "12px",
                                      fontWeight: "600",
                                      cursor: isCancelling ? "not-allowed" : "pointer",
                                      opacity: isCancelling ? 0.6 : 1,
                                    }}
                                    title="Cancel leave request"
                                  >
                                    <Trash2 size={12} />
                                    Cancel
                                  </button>
                                ) : (
                                  <span style={{ fontSize: "12px", color: "#cbd5e1" }}>—</span>
                                )}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {/* PAGINATION CONTROLS */}
                  {myRequestsPagination.totalPages > 1 && (
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      paddingTop: "14px",
                      marginTop: "12px",
                      borderTop: "1px solid #f1f5f9",
                      flexWrap: "wrap",
                      gap: "12px",
                    }}>
                      <span style={{ fontSize: "12px", color: "#64748b" }}>
                        Showing <strong>{(myCurrentPage - 1) * myRequestsPagination.limit + 1}</strong> to{" "}
                        <strong>{Math.min(myCurrentPage * myRequestsPagination.limit, myRequestsPagination.totalRecords)}</strong> of{" "}
                        <strong>{myRequestsPagination.totalRecords}</strong> requests
                      </span>

                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <button
                          type="button"
                          onClick={() => loadMyRequests(myCurrentPage - 1)}
                          disabled={myCurrentPage <= 1 || loadingMyRequests}
                          style={{
                            padding: "5px 10px",
                            fontSize: "12px",
                            fontWeight: "600",
                            borderRadius: "6px",
                            border: "1px solid #cbd5e1",
                            background: myCurrentPage <= 1 ? "#f8fafc" : "#ffffff",
                            color: myCurrentPage <= 1 ? "#94a3b8" : "#334155",
                            cursor: myCurrentPage <= 1 ? "not-allowed" : "pointer",
                            outline: "none",
                          }}
                        >
                          Previous
                        </button>
                        <span style={{ fontSize: "12px", fontWeight: "600", color: "#334155", padding: "0 4px" }}>
                          {myCurrentPage} / {myRequestsPagination.totalPages}
                        </span>
                        <button
                          type="button"
                          onClick={() => loadMyRequests(myCurrentPage + 1)}
                          disabled={myCurrentPage >= myRequestsPagination.totalPages || loadingMyRequests}
                          style={{
                            padding: "5px 10px",
                            fontSize: "12px",
                            fontWeight: "600",
                            borderRadius: "6px",
                            border: "1px solid #cbd5e1",
                            background: myCurrentPage >= myRequestsPagination.totalPages ? "#f8fafc" : "#ffffff",
                            color: myCurrentPage >= myRequestsPagination.totalPages ? "#94a3b8" : "#334155",
                            cursor: myCurrentPage >= myRequestsPagination.totalPages ? "not-allowed" : "pointer",
                            outline: "none",
                          }}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* SECTION 2: REVIEW REQUESTS (MANAGERS / UPPER HIERARCHY ONLY) */}
      {canReviewLeaves && activeTab === "review" && (
        <div style={{ marginTop: "24px" }}>
          <div style={s.card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", flexWrap: "wrap", gap: "10px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <div style={{ width: "32px", height: "32px", borderRadius: "8px", background: "rgba(37,99,235,0.1)", display: "flex", alignItems: "center", justifyContent: "center", color: THEME }}>
                  <ShieldCheck size={18} />
                </div>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <h3 style={{ ...s.cardTitle, margin: 0 }}>Team Approval Requests</h3>
                    <span style={{ fontSize: "11px", fontWeight: "700", padding: "2px 8px", borderRadius: "12px", background: pendingReviews.length > 0 ? "#fef3c7" : "#f1f5f9", color: pendingReviews.length > 0 ? "#b45309" : "#64748b" }}>
                      {pendingReviews.length} Pending
                    </span>
                  </div>
                  <p style={{ margin: "2px 0 0", fontSize: "12px", color: "#64748b" }}>
                    Leave requests submitted by team members awaiting your review and approval decision.
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="ado-refresh-btn"
                onClick={loadPendingReviews}
                disabled={loadingReviews}
                style={s.refreshBtn}
                title="Refresh Review Requests"
              >
                <RefreshCw size={15} style={loadingReviews ? { animation: "spin 0.8s linear infinite" } : {}} />
              </button>
            </div>

            {loadingReviews ? (
              <div style={{ padding: "30px", textAlign: "center", color: "#64748b", fontSize: "13px" }}>
                <RefreshCw size={18} style={{ animation: "spin 1s linear infinite", display: "inline-block", marginRight: "8px" }} />
                Loading pending review requests…
              </div>
            ) : pendingReviews.length === 0 ? (
              <div style={{ padding: "24px", background: "#f8fafc", borderRadius: "8px", border: "1px dashed #cbd5e1", textAlign: "center", color: "#64748b", fontSize: "13px" }}>
                <CheckCircle2 size={24} color="#10b981" style={{ display: "block", margin: "0 auto 8px" }} />
                <strong>All caught up!</strong>
                <div style={{ marginTop: "4px", fontSize: "12px", color: "#94a3b8" }}>
                  There are no pending leave requests awaiting your review.
                </div>
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                  <thead>
                    <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                      <th style={{ padding: "10px 14px", textAlign: "left", fontWeight: "600", color: "#475569" }}>REQUESTER</th>
                      <th style={{ padding: "10px 14px", textAlign: "left", fontWeight: "600", color: "#475569" }}>REQUESTED DATES</th>
                      <th style={{ padding: "10px 14px", textAlign: "left", fontWeight: "600", color: "#475569" }}>DURATION</th>
                      <th style={{ padding: "10px 14px", textAlign: "left", fontWeight: "600", color: "#475569" }}>REASON / COMMENT</th>
                      <th style={{ padding: "10px 14px", textAlign: "left", fontWeight: "600", color: "#475569" }}>SUBMITTED ON</th>
                      <th style={{ padding: "10px 14px", textAlign: "center", fontWeight: "600", color: "#475569" }}>ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingReviews.map((reqItem) => {
                      const isBusy = reviewingId === reqItem.requestId;
                      return (
                        <tr key={reqItem.requestId} style={{ borderBottom: "1px solid #f1f5f9" }}>
                          <td style={{ padding: "12px 14px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                              <div style={{ width: "30px", height: "30px", borderRadius: "50%", background: "#e0f2fe", color: "#0284c7", fontWeight: "700", fontSize: "12px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                {(reqItem.userName || reqItem.userEmail).charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                  <span style={{ fontWeight: "600", color: "#0f172a" }}>{reqItem.userName || "Team Member"}</span>
                                  {reqItem.isTopLevel === 1 && (
                                    <span style={{ fontSize: "10px", fontWeight: "700", padding: "2px 7px", borderRadius: "10px", background: "#ede9fe", color: "#6d28d9" }} title="Routed directly for Administrator review.">
                                      Direct Admin Review
                                    </span>
                                  )}
                                </div>
                                <div style={{ fontSize: "11px", color: "#64748b" }}>{reqItem.userEmail}</div>
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: "12px 14px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "6px", fontWeight: "600", color: "#1e293b" }}>
                              <Calendar size={14} color="#64748b" />
                              {reqItem.fromDate}
                              {reqItem.fromDate !== reqItem.toDate && ` → ${reqItem.toDate}`}
                            </div>
                          </td>
                          <td style={{ padding: "12px 14px" }}>
                            <span style={{ fontSize: "12px", fontWeight: "600", padding: "3px 8px", borderRadius: "6px", background: "#f1f5f9", color: "#475569" }}>
                              {reqItem.daysCount} {reqItem.daysCount === 1 ? "day" : "days"}
                            </span>
                          </td>
                          <td style={{ padding: "12px 14px", color: "#334155" }}>
                            {reqItem.reason || "Paid Time Off"}
                          </td>
                          <td style={{ padding: "12px 14px", color: "#64748b", fontSize: "12px" }}>
                            {new Date(reqItem.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                          </td>
                          <td style={{ padding: "12px 14px", textAlign: "center" }}>
                            <div style={{ display: "flex", justifyContent: "center", gap: "8px" }}>
                              <button
                                type="button"
                                onClick={() => openApproveModal(reqItem)}
                                disabled={isBusy}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "5px",
                                  padding: "6px 12px",
                                  borderRadius: "6px",
                                  border: "none",
                                  background: "#10b981",
                                  color: "#ffffff",
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  cursor: isBusy ? "not-allowed" : "pointer",
                                  opacity: isBusy ? 0.6 : 1,
                                }}
                              >
                                <Check size={14} />
                                Approve
                              </button>
                              <button
                                type="button"
                                onClick={() => openRejectModal(reqItem)}
                                disabled={isBusy}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "5px",
                                  padding: "6px 12px",
                                  borderRadius: "6px",
                                  border: "1px solid #fecaca",
                                  background: "#fee2e2",
                                  color: "#dc2626",
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  cursor: isBusy ? "not-allowed" : "pointer",
                                  opacity: isBusy ? 0.6 : 1,
                                }}
                              >
                                <X size={14} />
                                Reject
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}



      {/* REJECT MODAL */}
      {rejectModal.open && rejectModal.request && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.6)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "16px",
          }}
          onClick={() => setRejectModal({ open: false, request: null, comment: "" })}
        >
          <div
            style={{
              background: "#ffffff",
              borderRadius: "12px",
              padding: "24px",
              width: "100%",
              maxWidth: "480px",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
              <div style={{ width: "36px", height: "36px", borderRadius: "50%", background: "#fee2e2", display: "flex", alignItems: "center", justifyContent: "center", color: "#dc2626" }}>
                <XCircle size={20} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: "16px", fontWeight: "700", color: "#0f172a" }}>Reject Leave Request</h3>
                <p style={{ margin: "2px 0 0", fontSize: "12px", color: "#64748b" }}>
                  For {rejectModal.request.userName || rejectModal.request.userEmail}
                </p>
              </div>
            </div>

            <div style={{ padding: "10px 12px", background: "#f8fafc", borderRadius: "8px", fontSize: "12px", marginBottom: "16px", border: "1px solid #e2e8f0" }}>
              <div><strong>Dates:</strong> {rejectModal.request.fromDate}{rejectModal.request.fromDate !== rejectModal.request.toDate && ` → ${rejectModal.request.toDate}`} ({rejectModal.request.daysCount} days)</div>
              <div style={{ marginTop: "4px" }}><strong>Reason:</strong> {rejectModal.request.reason || "Paid Time Off"}</div>
            </div>

            <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#334155", marginBottom: "6px" }}>
              Rejection Reason / Comments <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <textarea
              rows={4}
              placeholder="e.g. Critical project deadline, insufficient sprint coverage, please coordinate with team lead..."
              value={rejectModal.comment}
              onChange={(e) => setRejectModal((prev) => ({ ...prev, comment: e.target.value }))}
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: "8px",
                border: "1px solid #cbd5e1",
                fontSize: "13px",
                boxSizing: "border-box",
                outline: "none",
                fontFamily: "inherit",
              }}
            />

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "18px" }}>
              <button
                type="button"
                onClick={() => setRejectModal({ open: false, request: null, comment: "" })}
                style={{
                  padding: "8px 16px",
                  borderRadius: "6px",
                  border: "1px solid #cbd5e1",
                  background: "#ffffff",
                  color: "#475569",
                  fontWeight: "600",
                  fontSize: "13px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmReject}
                disabled={reviewingId === rejectModal.request.requestId}
                style={{
                  padding: "8px 18px",
                  borderRadius: "6px",
                  border: "none",
                  background: "#dc2626",
                  color: "#ffffff",
                  fontWeight: "600",
                  fontSize: "13px",
                  cursor: reviewingId === rejectModal.request.requestId ? "not-allowed" : "pointer",
                  opacity: reviewingId === rejectModal.request.requestId ? 0.7 : 1,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                {reviewingId === rejectModal.request.requestId ? (
                  <>
                    <RefreshCw size={14} style={{ animation: "spin 1s linear infinite" }} />
                    Rejecting…
                  </>
                ) : (
                  <>
                    <X size={14} />
                    Confirm Rejection
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* APPROVE MODAL (CUSTOM ENTERPRISE CONFIRMATION) */}
      {approveModal.open && approveModal.request && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.6)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "16px",
          }}
          onClick={() => setApproveModal({ open: false, request: null })}
        >
          <div
            style={{
              background: "#ffffff",
              borderRadius: "12px",
              padding: "24px",
              width: "100%",
              maxWidth: "480px",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
              <div style={{ width: "40px", height: "40px", borderRadius: "50%", background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center", color: "#16a34a" }}>
                <CheckCircle2 size={22} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: "16px", fontWeight: "700", color: "#0f172a" }}>Approve Leave Request</h3>
                <p style={{ margin: "2px 0 0", fontSize: "12px", color: "#64748b" }}>
                  For {approveModal.request.userName || approveModal.request.userEmail}
                </p>
              </div>
            </div>

            <div style={{ padding: "12px 14px", background: "#f8fafc", borderRadius: "8px", fontSize: "13px", marginBottom: "16px", border: "1px solid #e2e8f0" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                <span style={{ color: "#64748b" }}>Requester:</span>
                <span style={{ fontWeight: "600", color: "#0f172a" }}>{approveModal.request.userName || approveModal.request.userEmail}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                <span style={{ color: "#64748b" }}>Requested Dates:</span>
                <span style={{ fontWeight: "600", color: "#0f172a" }}>
                  {approveModal.request.fromDate}{approveModal.request.fromDate !== approveModal.request.toDate && ` → ${approveModal.request.toDate}`} ({approveModal.request.daysCount} {approveModal.request.daysCount === 1 ? "day" : "days"})
                </span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "#64748b" }}>Reason / Note:</span>
                <span style={{ fontWeight: "500", color: "#334155" }}>{approveModal.request.reason || "Paid Time Off"}</span>
              </div>
            </div>

            <p style={{ margin: "0 0 20px", fontSize: "12.5px", color: "#64748b", lineHeight: "1.5" }}>
              Are you sure you want to approve this leave request? This will confirm the leave dates and update team availability.
            </p>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
              <button
                type="button"
                onClick={() => setApproveModal({ open: false, request: null })}
                style={{
                  padding: "8px 16px",
                  borderRadius: "6px",
                  border: "1px solid #cbd5e1",
                  background: "#ffffff",
                  color: "#475569",
                  fontWeight: "600",
                  fontSize: "13px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmApprove}
                disabled={reviewingId === approveModal.request.requestId}
                style={{
                  padding: "8px 18px",
                  borderRadius: "6px",
                  border: "none",
                  background: "#10b981",
                  color: "#ffffff",
                  fontWeight: "600",
                  fontSize: "13px",
                  cursor: reviewingId === approveModal.request.requestId ? "not-allowed" : "pointer",
                  opacity: reviewingId === approveModal.request.requestId ? 0.7 : 1,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                {reviewingId === approveModal.request.requestId ? (
                  <>
                    <RefreshCw size={14} style={{ animation: "spin 0.8s linear infinite" }} />
                    Approving…
                  </>
                ) : (
                  <>
                    <Check size={15} />
                    Confirm Approval
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CANCEL PENDING REQUEST MODAL */}
      {cancelModal.open && cancelModal.request && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.6)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "16px",
          }}
          onClick={() => setCancelModal({ open: false, request: null })}
        >
          <div
            style={{
              background: "#ffffff",
              borderRadius: "12px",
              padding: "24px",
              width: "100%",
              maxWidth: "440px",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
              <div style={{ width: "40px", height: "40px", borderRadius: "50%", background: "#fee2e2", display: "flex", alignItems: "center", justifyContent: "center", color: "#dc2626" }}>
                <AlertCircle size={22} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: "16px", fontWeight: "700", color: "#0f172a" }}>
                  {cancelModal.request.status === "APPROVED" ? "Cancel Approved Leave" : "Cancel Leave Request"}
                </h3>
                <p style={{ margin: "2px 0 0", fontSize: "12px", color: "#64748b" }}>
                  {cancelModal.request.status === "APPROVED" ? "Cancel your approved upcoming leave schedule" : "Withdraw your submitted leave request"}
                </p>
              </div>
            </div>

            <div style={{ padding: "10px 12px", background: "#f8fafc", borderRadius: "8px", fontSize: "12px", marginBottom: "16px", border: "1px solid #e2e8f0" }}>
              <div><strong>Dates:</strong> {cancelModal.request.fromDate}{cancelModal.request.fromDate !== cancelModal.request.toDate && ` → ${cancelModal.request.toDate}`} ({cancelModal.request.daysCount} {cancelModal.request.daysCount === 1 ? "day" : "days"})</div>
              <div style={{ marginTop: "4px" }}><strong>Reason:</strong> {cancelModal.request.reason || "Paid Time Off"}</div>
            </div>

            <p style={{ margin: "0 0 20px", fontSize: "13px", color: "#475569", lineHeight: "1.5" }}>
              {cancelModal.request.status === "APPROVED"
                ? "Are you sure you want to cancel this approved upcoming leave? The approved leave dates will be cancelled and removed from your absent calendar."
                : "Are you sure you want to cancel this pending leave request? This action cannot be undone."}
            </p>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
              <button
                type="button"
                onClick={() => setCancelModal({ open: false, request: null })}
                style={{
                  padding: "8px 16px",
                  borderRadius: "6px",
                  border: "1px solid #cbd5e1",
                  background: "#ffffff",
                  color: "#475569",
                  fontWeight: "600",
                  fontSize: "13px",
                  cursor: "pointer",
                }}
              >
                {cancelModal.request.status === "APPROVED" ? "Keep Leave" : "Keep Request"}
              </button>
              <button
                type="button"
                onClick={handleConfirmCancel}
                disabled={cancellingRequestId === cancelModal.request.requestId}
                style={{
                  padding: "8px 18px",
                  borderRadius: "6px",
                  border: "none",
                  background: "#dc2626",
                  color: "#ffffff",
                  fontWeight: "600",
                  fontSize: "13px",
                  cursor: cancellingRequestId === cancelModal.request.requestId ? "not-allowed" : "pointer",
                  opacity: cancellingRequestId === cancelModal.request.requestId ? 0.7 : 1,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                {cancellingRequestId === cancelModal.request.requestId
                  ? "Cancelling…"
                  : (cancelModal.request.status === "APPROVED" ? "Yes, Cancel Approved Leave" : "Yes, Cancel Request")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRMED PTO RECORD MODAL */}
      {deleteModal.open && deleteModal.item && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.6)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "16px",
          }}
          onClick={() => setDeleteModal({ open: false, item: null })}
        >
          <div
            style={{
              background: "#ffffff",
              borderRadius: "12px",
              padding: "24px",
              width: "100%",
              maxWidth: "440px",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
              <div style={{ width: "40px", height: "40px", borderRadius: "50%", background: "#fee2e2", display: "flex", alignItems: "center", justifyContent: "center", color: "#dc2626" }}>
                <Trash2 size={20} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: "16px", fontWeight: "700", color: "#0f172a" }}>Cancel Absent Record</h3>
                <p style={{ margin: "2px 0 0", fontSize: "12px", color: "#64748b" }}>
                  Remove confirmed PTO date
                </p>
              </div>
            </div>

            <div style={{ padding: "10px 12px", background: "#f8fafc", borderRadius: "8px", fontSize: "12px", marginBottom: "16px", border: "1px solid #e2e8f0" }}>
              <div><strong>Date:</strong> {deleteModal.item.absentDate}</div>
              <div style={{ marginTop: "4px" }}><strong>Reason:</strong> {deleteModal.item.reason || "PTO"}</div>
            </div>

            <p style={{ margin: "0 0 20px", fontSize: "13px", color: "#475569", lineHeight: "1.5" }}>
              Are you sure you want to cancel this absent date record?
            </p>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
              <button
                type="button"
                onClick={() => setDeleteModal({ open: false, item: null })}
                style={{
                  padding: "8px 16px",
                  borderRadius: "6px",
                  border: "1px solid #cbd5e1",
                  background: "#ffffff",
                  color: "#475569",
                  fontWeight: "600",
                  fontSize: "13px",
                  cursor: "pointer",
                }}
              >
                Keep Record
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={deletingId === deleteModal.item.absentId}
                style={{
                  padding: "8px 18px",
                  borderRadius: "6px",
                  border: "none",
                  background: "#dc2626",
                  color: "#ffffff",
                  fontWeight: "600",
                  fontSize: "13px",
                  cursor: deletingId === deleteModal.item.absentId ? "not-allowed" : "pointer",
                  opacity: deletingId === deleteModal.item.absentId ? 0.7 : 1,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                {deletingId === deleteModal.item.absentId ? "Removing…" : "Yes, Remove"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* =====================================================
   STYLES OBJECT — 100% MATCHING Holidays.js
===================================================== */
const s = {
  page: {
    minHeight: "100vh",
    boxSizing: "border-box",
    maxWidth: "100%",
    padding: "clamp(12px, 4vw, 24px)",
  },
  header: { marginBottom: "20px" },
  breadcrumb: {
    fontSize: "12px",
    color: "#94a3b8",
    marginBottom: "6px",
  },
  title: {
    margin: 0,
    fontSize: "26px",
    fontWeight: "700",
    color: "#0f172a",
  },
  sub: {
    color: "#64748b",
    marginTop: "5px",
    fontSize: "14px",
  },
  backLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "13px",
    color: THEME,
    cursor: "pointer",
    fontWeight: "600",
    background: "none",
    border: "none",
    padding: 0,
    marginBottom: "20px",
    outline: "none",
  },
  gridContainer: {
    display: "flex",
    gap: "20px",
    flexWrap: "wrap",
    width: "100%",
    boxSizing: "border-box",
  },
  formColumn: {
    flex: "0 0 380px",
    maxWidth: "100%",
    boxSizing: "border-box",
  },
  tableColumn: {
    flex: "1 1 500px",
    minWidth: "320px",
    maxWidth: "100%",
    boxSizing: "border-box",
  },
  card: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "16px",
    padding: "clamp(16px, 4vw, 24px)",
    boxShadow: "0 1px 3px rgba(15,23,42,0.06)",
    boxSizing: "border-box",
    height: "100%",
  },
  cardTitle: {
    margin: "0 0 16px 0",
    fontSize: "16px",
    fontWeight: "600",
    color: "#0f172a",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  label: {
    fontSize: "11px",
    fontWeight: "700",
    color: "#475569",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  input: {
    padding: "10px 12px",
    border: "1px solid #cbd5e1",
    borderRadius: "8px",
    fontSize: "14px",
    background: "#ffffff",
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
    color: "#0f172a",
    height: "42px",
  },
  submitBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "10px 20px",
    border: "none",
    borderRadius: "8px",
    background: THEME,
    color: "#ffffff",
    fontSize: "14px",
    fontWeight: "600",
    fontFamily: "inherit",
    outline: "none",
    marginTop: "8px",
    height: "42px",
  },
  pillsGroup: {
    display: "flex",
    gap: "6px",
    background: "#f8fafc",
    padding: "4px",
    borderRadius: "10px",
    border: "1px solid #e2e8f0",
  },
  pillBtn: {
    flex: 1,
    padding: "8px 10px",
    fontSize: "12px",
    fontWeight: "500",
    borderRadius: "8px",
    border: "none",
    background: "transparent",
    color: "#64748b",
    cursor: "pointer",
    transition: "all 0.15s ease",
  },
  pillActive: {
    background: "#fff",
    color: THEME,
    fontWeight: "600",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  },
  infoBox: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 14px",
    background: THEME_LIGHT,
    borderRadius: "8px",
    fontSize: "13px",
    color: THEME,
    border: `1px solid rgba(37, 99, 235, 0.2)`,
  },
  tableHeaderRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
    flexWrap: "wrap",
    gap: "10px",
  },
  refreshBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "36px",
    height: "36px",
    border: "1px solid #cbd5e1",
    borderRadius: "8px",
    background: "#ffffff",
    color: "#64748b",
    cursor: "pointer",
    outline: "none",
  },
  tableContainer: {
    width: "100%",
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    textAlign: "left",
  },
  th: {
    padding: "10px 12px",
    fontSize: "11px",
    fontWeight: "700",
    color: "#475569",
    textTransform: "uppercase",
    borderBottom: "2px solid #e2e8f0",
  },
  thActions: {
    padding: "10px 12px",
    fontSize: "11px",
    fontWeight: "700",
    color: "#475569",
    textTransform: "uppercase",
    borderBottom: "2px solid #e2e8f0",
    textAlign: "center",
    width: "80px",
  },
  tr: {
    borderBottom: "1px solid #f1f5f9",
    transition: "background 0.15s ease",
  },
  td: {
    padding: "12px",
    fontSize: "14px",
    color: "#64748b",
  },
  tdBold: {
    padding: "12px",
    fontSize: "14px",
    fontWeight: "600",
    color: "#0f172a",
  },
  tdEmail: {
    padding: "12px",
    fontSize: "13px",
    color: "#64748b",
  },
  tdActions: {
    padding: "12px",
    textAlign: "center",
  },
  badgeAbsent: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: "12px",
    background: "#fee2e2",
    color: "#dc2626",
    fontSize: "11px",
    fontWeight: "600",
  },
  deleteBtn: {
    background: "none",
    border: "none",
    color: "#94a3b8",
    cursor: "pointer",
    padding: "6px",
    borderRadius: "6px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.15s",
    outline: "none",
  },
  skeletonBar: {
    height: "14px",
    background: "#e2e8f0",
    borderRadius: "4px",
    width: "100px",
    animation: "pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite",
  },
  overlayLoader: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(255, 255, 255, 0.72)",
    backdropFilter: "blur(2px)",
    zIndex: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    color: THEME,
    fontWeight: "600",
    fontSize: "13px",
    borderRadius: "8px",
  },
  loadingBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    padding: "40px",
    color: "#64748b",
    fontSize: "14px",
  },
  loader: {
    width: "20px",
    height: "20px",
    border: "2.5px solid #dbeafe",
    borderTop: "2.5px solid #2563eb",
    borderRadius: "50%",
    boxSizing: "border-box",
  },
  emptyBox: {
    padding: "40px",
    textAlign: "center",
    color: "#94a3b8",
    fontSize: "14px",
    background: "#f8fafc",
    borderRadius: "12px",
    border: "1px dashed #e2e8f0",
  },
  errorBox: {
    background: "#fee2e2",
    color: "#991b1b",
    padding: "12px 14px",
    borderRadius: "10px",
    fontSize: "14px",
    marginBottom: "16px",
  },
  successBox: {
    background: "#f0fdf4",
    color: "#166534",
    padding: "12px 14px",
    borderRadius: "10px",
    fontSize: "14px",
    marginBottom: "16px",
  },
  panel: {
    position: "absolute",
    left: 0,
    background: "#ffffff",
    border: "1px solid #cbd5e1",
    borderRadius: "10px",
    boxShadow: "0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)",
    zIndex: 1000,
  },
};

const dp = {
  trigger: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    padding: "10px 12px",
    border: "1px solid #cbd5e1",
    borderRadius: "8px",
    fontSize: "14px",
    background: "#ffffff",
    color: "#0f172a",
    fontFamily: "inherit",
    cursor: "pointer",
    textAlign: "left",
    transition: "border-color 0.15s, box-shadow 0.15s",
    height: "42px",
    boxSizing: "border-box",
    gap: "8px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "8px",
  },
  navBtn: {
    background: "none",
    border: "1px solid #e2e8f0",
    borderRadius: "6px",
    width: "24px",
    height: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "#64748b",
    outline: "none",
    transition: "background 0.1s",
  },
  navTitle: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#0f172a",
  },
  weekdaysGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    textAlign: "center",
    marginBottom: "4px",
  },
  weekday: {
    fontSize: "11px",
    fontWeight: "600",
    color: "#94a3b8",
    padding: "2px 0",
  },
  daysGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: "2px",
    marginBottom: "6px",
  },
  dayCell: {
    aspectRatio: "1",
    border: "none",
    borderRadius: "6px",
    fontSize: "12px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    outline: "none",
    transition: "background 0.1s, color 0.1s",
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    borderTop: "1px solid #f1f5f9",
    paddingTop: "8px",
    marginTop: "2px",
  },
  footerBtn: {
    background: "none",
    border: "none",
    fontSize: "11px",
    cursor: "pointer",
    color: "#64748b",
    padding: "4px 6px",
    borderRadius: "4px",
    outline: "none",
  },
};
